import {
  Button,
  Col,
  Menu,
  Row,
  SearchField,
  TinyTable,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideCheck,
  LucideEllipsis,
  LucideGlobe,
  LucideHash,
  LucidePackage,
  LucidePlusCircle,
  LucideSquarePen,
  LucideTrash,
} from 'lucide-react';

import { ComplianceItemJurisdictionCodeRecord } from '../../_graphql/queryComplianceItemJurisdictionCodes';
import * as itemCodesState from '../../_state/itemJurisdictionCodes.state';
import { useTranslations } from '../../i18n/useTranslations';
import { SettingsSubmodule } from '../_layouts/SettingsSubmodule';
import { SettingsContent } from '../_shared/SettingsContent';

import { ItemJurisdictionCodeFormDialog } from './ItemJurisdictionCodeFormDialog';

type CodeRowMenuProps = { row: ComplianceItemJurisdictionCodeRecord };

const CodeRowMenu = (props: CodeRowMenuProps) => {
  const t = useTranslations();
  const setEditingCode = useSetAtom(itemCodesState.$editingItemCode);
  const setDialogOpen = useSetAtom(itemCodesState.$isItemCodeDialogOpen);
  const deleteCode = useSetAtom(itemCodesState.$deleteItemJurisdictionCode);
  const toast = useToast();

  const handleDelete = async () => {
    try {
      await deleteCode({ id: props.row.id });
      toast.success(t.complianceView.itemCodes.dialog.messages.deleteSuccess);
    } catch (error) {
      toast.error(
        t.complianceView.itemCodes.dialog.messages.deleteError(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    }
  };

  return (
    <div className="@CodeRowMenu contents">
      <Menu
        trigger={
          <Button
            className="px-2"
            size="small"
            color="neutral"
            variant="outline"
            icon={<LucideEllipsis />}
          />
        }
        items={[
          {
            label: t.complianceView.common.actions.edit,
            icon: <LucideSquarePen />,
            onAction: () => {
              setEditingCode(props.row.id);
              setDialogOpen(true);
            },
          },
          {
            label: t.complianceView.common.actions.delete,
            icon: <LucideTrash />,
            className: 'text-failure',
            secondStep: ({ close }) => (
              <Col gap3>
                <div className="text-center text-sm text-neutral/70">
                  {t.complianceView.common.confirmDelete}
                </div>
                <Row gap2>
                  <Button variant="outline" color="neutral" onPress={close}>
                    {t.complianceView.common.actions.cancel}
                  </Button>
                  <Button
                    variant="outline"
                    color="failure"
                    icon={<LucideTrash />}
                    onPress={() => {
                      close();
                      handleDelete();
                    }}
                  >
                    {t.complianceView.common.yesDelete}
                  </Button>
                </Row>
              </Col>
            ),
          },
        ]}
      />
    </div>
  );
};

export const ItemJurisdictionCodesView = () => {
  const t = useTranslations();
  const codes = useAtomValue(itemCodesState.$itemJurisdictionCodes);
  const [searchTerm, setSearchTerm] = useAtom(
    itemCodesState.$itemCodesSearchTerm.value,
  );
  const setDialogOpen = useSetAtom(itemCodesState.$isItemCodeDialogOpen);
  const setEditingCode = useSetAtom(itemCodesState.$editingItemCode);

  return (
    <Col className="@ItemJurisdictionCodesView h-full">
      <SettingsSubmodule
        title={t.complianceView.itemCodes.title}
        subtitle={t.complianceView.itemCodes.subtitle}
        tabs={null}
      >
        <SettingsContent className="h-full">
          <Col gap4 className="h-full">
            <Row gap2>
              <SearchField
                value={searchTerm}
                onChange={setSearchTerm}
                autoFocus
                placeholder={t.complianceView.itemCodes.search.placeholder}
              />
              <Button
                className="ml-auto"
                icon={<LucidePlusCircle />}
                onPress={() => {
                  setEditingCode(null);
                  setDialogOpen(true);
                }}
              >
                {t.complianceView.itemCodes.actions.addCode}
              </Button>
            </Row>
            <ItemJurisdictionCodeFormDialog />
            <TinyTable
              rowKey={(row) => row.id}
              isLoading={codes.isLoading}
              error={codes.error}
              columns={[
                {
                  key: 'item',
                  label: t.complianceView.itemCodes.columns.item,
                  icon: LucidePackage,
                  cell: (row) => (
                    <div className="truncate font-medium">
                      {row.itemName ?? row.itemId}
                    </div>
                  ),
                },
                {
                  key: 'serviceType',
                  label: t.complianceView.itemCodes.columns.serviceType,
                  icon: LucideGlobe,
                  width: 200,
                  cell: (row) => (
                    <div className="truncate">{row.serviceType}</div>
                  ),
                },
                {
                  key: 'code',
                  label: t.complianceView.itemCodes.columns.code,
                  icon: LucideHash,
                  width: 120,
                  cell: (row) => (
                    <code className="rounded bg-neutral/5 px-1.5 py-0.5 text-xs">
                      {row.code}
                    </code>
                  ),
                },
                {
                  key: 'reportable',
                  label: t.complianceView.itemCodes.columns.reportable,
                  icon: LucideCheck,
                  width: 100,
                  cell: (row) => (
                    <span
                      className={cn(
                        row.isReportable && 'text-success',
                        !row.isReportable && 'text-neutral/40',
                      )}
                    >
                      {row.isReportable
                        ? t.complianceView.common.yes
                        : t.complianceView.common.no}
                    </span>
                  ),
                },
                {
                  key: 'actions',
                  label: null,
                  preventRowClick: true,
                  width: 'max-content',
                  cell: (row) => <CodeRowMenu row={row} />,
                },
              ]}
              data={codes.data ?? []}
            />
          </Col>
        </SettingsContent>
      </SettingsSubmodule>
    </Col>
  );
};
